// ---------------------------------------------------------------------------
// GR4J model (Génie Rural, daily, 4 parameters) — Perrin, Michel,
// Andréassian (2003), "Improvement of a parsimonious model for streamflow
// simulation", J. Hydrology 279.
//
// The production tank/percolation/unit hydrograph formulation was confirmed
// by decompiling EvalHid's real calculation engine (Evalhid.dll, .NET 6, via
// ilspycmd — see class Gr4j.calculo_gr4j()), which matches INRAE's official
// documentation except for two real differences:
//
// 1. P/E neutralization: on a dry step (P≤E), the net ETP demand is
//    `En=E-P`. An earlier version of the real code did `En=E-Pn` with `Pn`
//    left uninitialized (defaulting to 0 from the step's initialization)
//    on that branch — i.e. it always subtracted 0, treating the full
//    potential ETP as demand. Confirmed fixed in the current real source
//    (2026-08, see CLAUDE.md's engine-source audit — `Gr4j.cs` now uses
//    `p`, not `pn`, in this branch).
// 2. The routing tank (R, with X2/X3) and the UH1 hydrograph (90%) are
//    ALWAYS calculated — including the exchange term F, which also feeds
//    into the direct (UH2) branch (`qDirect = outputUh2 + F`, not just
//    `outputUh2`). Only the FINAL SUM is conditional on whether the
//    sub-basin has a real aquifer assigned (`cod_acuifero == "0"`, the real
//    "no aquifer" sentinel — see hbv.ts/temez.ts for the contrast with
//    those two models, which check a "-1" sentinel that never appears in
//    real data): without a real aquifer, `contribution = qBase + qDirect`;
//    with one, `contribution = qDirect` alone (qBase, still computed, is
//    assumed to flow to the aquifer's own aggregate balance instead — see
//    aggregation.ts). Confirmed in the real source (2026-08, see CLAUDE.md's
//    engine-source audit) — an earlier version of this port wrapped F/the
//    routing tank itself inside the aquifer check, which also silently
//    dropped the exchange term from `contribution` whenever a sub-basin had
//    a real aquifer assigned and X2≠0.
//
// Pumping (`bomb`, mm/step): subtracted from the routing tank (R)
// unconditionally, alongside its own inflow and exchange term, before Q is
// drawn from it (`Gr4j.cs:109`: `ro = Max(0, ro + q9 + fx2 - bomb)`).
// Resolved from the sub-basin's own `Ent_Bomb.csv` column
// (`SubBasin.pumpingCol`) and converted to mm/step before reaching here —
// see temez.ts's own header for the full resolution/conversion chain,
// shared by all 6 models. `null` only when the sub-basin has no pumping
// column of its own, or "Modelar bombeos" is unchecked.
// ---------------------------------------------------------------------------

export interface Gr4jParameters {
  /** X1 (mm): production tank capacity. */
  x1: number;
  /** X2 (mm/step): groundwater water exchange coefficient (additive, can be negative). */
  x2: number;
  /** X3 (mm): one-day capacity of the routing tank. */
  x3: number;
  /** X4 (step): unit hydrograph base time. */
  x4: number;
}

interface Gr4jState {
  /** So (mm): initial production tank content. */
  so: number;
  /** Ro (mm): initial routing tank content. */
  ro: number;
}

interface Gr4jDetail {
  [key: string]: Float64Array;
  /** S (mm): production tank after the step. */
  s: Float64Array;
  /** Pn (mm): net precipitation (after neutralizing with ETP). */
  pn: Float64Array;
  /** Etr (mm): actual evapotranspiration. */
  etr: Float64Array;
  /** Ps (mm): input into the production tank. */
  ps: Float64Array;
  /** Production tank percolation. */
  perc: Float64Array;
  /** R (mm): routing tank after the step — always calculated, see header note 2. */
  r: Float64Array;
  /** F (mm): groundwater exchange term — always calculated, see header note 2. */
  f: Float64Array;
  /** Direct component (UH2 hydrograph + F). */
  directRunoff: Float64Array;
  /** Routing/base component — always calculated, but only counted in `contribution` without a real aquifer (see header note 2). */
  baseRunoff: Float64Array;
}

interface Gr4jResult {
  /** Contribution (flow) per time step, mm. */
  contribution: Float64Array;
  /** Step-by-step internal detail series (see Sal_parciales.csv in the real .exe). */
  detail: Gr4jDetail;
}

/** S-curve of unit hydrograph 1 (base X4). */
function sh1(t: number, x4: number): number {
  if (t <= 0) return 0;
  if (t < x4) return (t / x4) ** 2.5;
  return 1;
}

/** S-curve of unit hydrograph 2 (base 2·X4). */
function sh2(t: number, x4: number): number {
  if (t <= 0) return 0;
  if (t < x4) return 0.5 * (t / x4) ** 2.5;
  if (t < 2 * x4) return 1 - 0.5 * (2 - t / x4) ** 2.5;
  return 1;
}

/**
 * `Gr4j.cs`'s `lnum`/`mnum` come from `Convert.ToInt32(double)`, which
 * rounds to the nearest integer with ties going to the EVEN one ("banker's
 * rounding") — unlike `Math.round`, which always rounds `.5` up. Only
 * matters when X4 (or 2·X4) lands on an exact `.5` (e.g. X4=2.5: .NET
 * gives 2, `Math.round` would give 3), changing the hydrograph's ordinate
 * count and, with it, the day-by-day distribution of the contribution
 * (not its total).
 */
function roundHalfToEven(x: number): number {
  const floor = Math.floor(x);
  const fraction = x - floor;
  if (fraction < 0.5) return floor;
  if (fraction > 0.5) return floor + 1;
  return floor % 2 === 0 ? floor : floor + 1;
}

/** Ordinates (i=0 → 1-step delay, i=n-1 → n-step delay) of a hydrograph from its S-curve. */
function ordinates(n: number, x4: number, sCurve: (t: number, x4: number) => number): Float64Array {
  const ord = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    ord[i] = sCurve(i + 1, x4) - sCurve(i, x4);
  }
  return ord;
}

/**
 * Advances a unit hydrograph's convolution by one step: `state[k]` is
 * what's pending to come out within k+1 steps. This step's own input is
 * mixed into the state BEFORE reading the output (`Gr4j.cs`: `vh[i_h] =
 * ord[i_h]*input + vh_0[i_h+1]`, then `q = vh[0]`) — so today's rain can
 * contribute to today's output (0-step minimum delay), not just future
 * steps. Shifts+adds within the state array itself.
 */
function advanceConvolution(state: Float64Array, ord: Float64Array, input: number): number {
  const n = state.length;
  for (let k = 0; k < n - 1; k++) {
    state[k] = ord[k] * input + state[k + 1];
  }
  state[n - 1] = ord[n - 1] * input;
  return state[0];
}

/**
 * Simulates the GR4J model for a single sub-basin. `precipitation` and
 * `etp` must already come adjusted (precipAdjustment/etpAdjustment applied) and aligned step
 * by step. The unit hydrograph pipelines start empty (0): EvalHid doesn't
 * define an initial state for them, only for the So/Ro tanks.
 * `hasAquifer` indicates whether the sub-basin has a real aquifer
 * assigned (see header note 2).
 */
export function calculateGr4j(
  precipitation: Float64Array,
  etp: Float64Array,
  parameters: Gr4jParameters,
  initialState: Gr4jState,
  hasAquifer: boolean,
  pumping: Float64Array | null
): Gr4jResult {
  const { x1, x2, x3, x4 } = parameters;
  const n = precipitation.length;
  const contribution = new Float64Array(n);
  const detail: Gr4jDetail = {
    s: new Float64Array(n),
    pn: new Float64Array(n),
    etr: new Float64Array(n),
    ps: new Float64Array(n),
    perc: new Float64Array(n),
    r: new Float64Array(n),
    f: new Float64Array(n),
    directRunoff: new Float64Array(n),
    baseRunoff: new Float64Array(n),
  };

  let s = initialState.so;
  let r = initialState.ro;

  const nUh1 = roundHalfToEven(x4) + 1;
  const nUh2 = roundHalfToEven(2 * x4) + 1;
  const ord1 = ordinates(nUh1, x4, sh1);
  const ord2 = ordinates(nUh2, x4, sh2);
  const stateUh1 = new Float64Array(nUh1);
  const stateUh2 = new Float64Array(nUh2);

  for (let t = 0; t < n; t++) {
    const p = Number.isFinite(precipitation[t]) ? precipitation[t] : 0;
    const e = Number.isFinite(etp[t]) ? etp[t] : 0;

    // P/E neutralization — see header note 1.
    let pn = 0;
    let en = 0;
    if (p > e) {
      pn = p - e;
    } else {
      en = e - p;
    }

    // Production tank: Pn comes in (tanh-type saturation) or En goes out, never both the same step.
    let ps = 0;
    if (pn > 0) {
      const tanhArg = Math.tanh(pn / x1);
      ps = (x1 * (1 - (s / x1) ** 2) * tanhArg) / (1 + (s / x1) * tanhArg);
    }
    let etr = 0;
    if (en > 0) {
      const tanhArg = Math.tanh(en / x1);
      etr = (s * (2 - s / x1) * tanhArg) / (1 + (1 - s / x1) * tanhArg);
    }
    s = Math.max(0, s + ps - etr);

    // Production tank percolation towards routing.
    const perc = Math.min(s, s * (1 - (1 + ((4 * s) / 9 / x1) ** 4) ** -0.25));
    s -= perc;
    const pr = perc + (pn - ps);

    // Unit hydrograph 2 (10%, direct) and the routing tank + unit hydrograph
    // 1 (90%, with its exchange term F) are both ALWAYS calculated — see
    // header note 2. Only the final sum (contribution) is conditional.
    const outputUh2 = advanceConvolution(stateUh2, ord2, 0.1 * pr);

    const f = x2 * (r / x3) ** 3.5;
    const outputUh1 = advanceConvolution(stateUh1, ord1, 0.9 * pr);
    const bomb = pumping && Number.isFinite(pumping[t]) ? pumping[t] : 0;
    r = Math.max(0, r + outputUh1 + f - bomb);
    const qBase = r * (1 - (1 + (r / x3) ** 4) ** -0.25);
    r -= qBase;

    const qDirect = Math.max(0, outputUh2 + f);

    contribution[t] = hasAquifer ? qDirect : qBase + qDirect;

    detail.s[t] = s;
    detail.pn[t] = pn;
    detail.etr[t] = etr;
    detail.ps[t] = ps;
    detail.perc[t] = perc;
    detail.r[t] = r;
    detail.f[t] = f;
    detail.directRunoff[t] = qDirect;
    detail.baseRunoff[t] = qBase;
  }

  return { contribution, detail };
}
